import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Modal } from '@koyomi/ui';
import { ApiError } from '../../lib/api.ts';

/** O que o 409 SLOT_UNAVAILABLE traz para o encaixe. */
export interface FitInInfo { reasons: string[]; overridable: string[]; allowed: boolean }
export interface FitInRequest { overrideReasons: string[]; durationMin?: number | null }

/** Lê o 409 de horário indisponível; null quando o erro é outro. */
export function readFitIn(err: unknown): FitInInfo | null {
  if (!(err instanceof ApiError) || err.code !== 'SLOT_UNAVAILABLE') return null;
  const d = (err.details as { reasons?: string[]; overridable?: string[]; fitInAllowed?: boolean } | undefined) ?? {};
  return { reasons: d.reasons ?? [], overridable: d.overridable ?? [], allowed: d.fitInAllowed !== false };
}

/** Dá para oferecer "encaixar mesmo assim": o negócio permite e pelo menos uma razão é sobrescrevível. */
export const canOfferFitIn = (info: FitInInfo | null): boolean => !!info && info.allowed && info.overridable.length > 0;

/**
 * "Encaixar mesmo assim?": lista o que a pessoa está aceitando furar, avisa o que não dá para furar (sala, cadastro)
 * e permite encurtar a duração quando é um serviço só. Confirmar reenvia o pedido com `fitIn`.
 */
export function FitInDialog({ info, singleServiceDuration, busy, onClose, onConfirm }: { info: FitInInfo; /** Duração do serviço quando é um só (habilita o campo); null para sequência. */ singleServiceDuration: number | null; busy: boolean; onClose: () => void; onConfirm: (fitIn: FitInRequest) => void }) {
  const { t } = useTranslation(['calendar', 'common']);
  const [duration, setDuration] = useState<string>(singleServiceDuration ? String(singleServiceDuration) : '');
  const blocked = info.reasons.filter((r) => !info.overridable.includes(r));
  const parsed = Number(duration);
  const customDuration = singleServiceDuration && duration !== '' && Number.isInteger(parsed) && parsed !== singleServiceDuration ? parsed : null;
  const durationInvalid = singleServiceDuration != null && duration !== '' && (!Number.isInteger(parsed) || parsed < 5 || parsed > singleServiceDuration);
  const ok = blocked.length === 0 && !durationInvalid;
  return (
    <Modal open onClose={onClose} title={t('calendar:fitIn.title')} size="sm" footer={<><Button onClick={onClose}>{t('calendar:fitIn.back')}</Button><Button variant="primary" disabled={!ok} loading={busy} onClick={() => onConfirm({ overrideReasons: info.overridable, durationMin: customDuration })}>{t('calendar:fitIn.confirm')}</Button></>}>
      <div className="k-stack">
        <p style={{ margin: 0 }}>{t('calendar:fitIn.intro')}</p>
        <ul className="k-list" style={{ margin: 0, paddingLeft: 18 }}>
          {info.overridable.map((r) => <li key={r}>{t(`calendar:fitIn.reasons.${r}`, { defaultValue: t(`calendar:reasons.${r}`, { defaultValue: r }) })}</li>)}
        </ul>
        {blocked.length > 0 && <div className="k-field__error">{t('calendar:fitIn.blocked', { reasons: blocked.map((r) => t(`calendar:reasons.${r}`, { defaultValue: r })).join(', ') })}</div>}
        {singleServiceDuration != null && (
          <Input label={t('calendar:fitIn.duration')} type="number" min={5} max={singleServiceDuration} step={5} value={duration} onChange={(e) => setDuration(e.target.value)} hint={t('calendar:fitIn.durationHint', { min: singleServiceDuration })} error={durationInvalid ? t('calendar:fitIn.durationInvalid', { min: singleServiceDuration }) : undefined} />
        )}
      </div>
    </Modal>
  );
}
